import React, { useState, useEffect } from 'react';
import {
  Eye, Edit3, FileText, ChevronDown, ChevronUp, MapPin, Truck, Users, Receipt, AlertCircle,
  Calendar, Clock, DollarSign, FileCheck, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Lock
} from 'lucide-react';
import { usePermissions } from '../../hooks/usePermissions';
import { MaskedValue, ProtectedField } from '../common/ProtectedField';
import { formatDateBR } from '../../utils/dateUtils';

interface OsDataGridProps {
  ordens: any[];
  loading: boolean;
  onView: (id: number) => void;
  onEdit: (id: number) => void;
  onPrint: (id: number) => void;
  onEmitCte?: (os: any) => void;
}

export const OsDataGrid: React.FC<OsDataGridProps> = ({
  ordens,
  loading,
  onView,
  onEdit,
  onPrint,
  onEmitCte,
}) => {
  const {
    canViewOrderPrices,
    canViewOrderRepasse,
    canViewOrderMargin,
    canEdit,
    canExport
  } = usePermissions();

  const [expandedRowIds, setExpandedRowIds] = useState<Set<number>>(new Set());
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(50);

  // Reset para a primeira página quando os dados mudarem
  useEffect(() => {
    setCurrentPage(1);
  }, [ordens.length]);

  const toggleRow = (id: number) => {
    setExpandedRowIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const formatCurrency = (val: any) => {
    const num = Number(val || 0);
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(num);
  };

  const formatDate = (dateStr: string) => {
    return formatDateBR(dateStr);
  };

  // Cálculos de Paginação
  const totalItems = ordens.length;
  const totalPages = Math.ceil(totalItems / pageSize) || 1;
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, totalItems);
  const paginatedOrdens = ordens.slice(startIndex, endIndex);

  // Janela de Números das Páginas (Máximo 10 botões numéricos por vez)
  const maxVisiblePages = 10;
  let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
  let endPage = startPage + maxVisiblePages - 1;

  if (endPage > totalPages) {
    endPage = totalPages;
    startPage = Math.max(1, endPage - maxVisiblePages + 1);
  }

  const pageNumbers: number[] = [];
  for (let i = startPage; i <= endPage; i++) {
    pageNumbers.push(i);
  }

  // Funções de Totais da Tabela (sobre todos os itens filtrados)
  const sumTotalOS = ordens.reduce((acc, item) => acc + (Number(item.total_os) || 0), 0);
  const sumTotalLiquido = ordens.reduce((acc, item) => acc + (Number(item.valor_liquido) || 0), 0);

  return (
    <div className="bg-white rounded-xl shadow-xs border border-slate-200 overflow-hidden flex flex-col">
      <div className="overflow-x-auto max-w-full">
        <table className="w-full text-xs text-left text-slate-600 border-collapse">
          <thead className="bg-slate-100 text-slate-700 font-bold border-b border-slate-200 uppercase text-3xs tracking-wider">
            <tr>
              <th className="px-3 py-3 text-center w-10 border-r border-slate-200">
                {/* Seta Espaço */}
              </th>
              <th className="px-3 py-3 text-center w-16 border-r border-slate-200">
                O.S
              </th>
              <th className="px-3 py-3 text-left border-r border-slate-200 min-w-[160px]">
                Cliente
              </th>
              <th className="px-3 py-3 text-center border-r border-slate-200 min-w-[110px]">
                Status
              </th>
              <th className="px-3 py-3 text-center border-r border-slate-200 min-w-[110px]">
                Data Atendimento
              </th>
              <th className="px-3 py-3 text-left border-r border-slate-200 min-w-[150px]">
                Veículo / Motorista
              </th>
              <th className="px-3 py-3 text-center border-r border-slate-200 min-w-[110px] bg-slate-200/50 font-black">
                Total O.S
              </th>
              <th className="px-3 py-3 text-center border-r border-slate-200 min-w-[110px] bg-blue-50/50 font-black text-blue-900">
                Valor Líquido
              </th>
              <th className="px-3 py-3 text-center sticky right-0 bg-slate-100 z-10 min-w-[120px]">
                Ações
              </th>
            </tr>
          </thead>

          <tbody className="divide-y divide-slate-200 bg-white">
            {loading ? (
              <tr>
                <td colSpan={9} className="px-6 py-12 text-center text-slate-400">
                  <div className="flex flex-col items-center gap-2">
                    <div className="w-6 h-6 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
                    <span>Carregando Ordens de Serviço...</span>
                  </div>
                </td>
              </tr>
            ) : ordens.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-6 py-12 text-center text-slate-400 font-medium">
                  Nenhuma ordem de serviço encontrada para os filtros selecionados.
                </td>
              </tr>
            ) : (
              paginatedOrdens.map((os) => {
                const isExpanded = expandedRowIds.has(os.id);
                const statusColor = os.status_cor || '#64748b';
                const statusName = os.status_nome || `Status #${os.status_id || 'N/A'}`;

                return (
                  <React.Fragment key={os.id}>
                    {/* Linha Principal Resumida */}
                    <tr
                      className={`hover:bg-slate-50 transition-colors cursor-pointer ${
                        isExpanded ? 'bg-red-50/20' : ''
                      }`}
                      onClick={() => toggleRow(os.id)}
                    >
                      {/* Seta para Expandir */}
                      <td className="px-2 py-3 text-center border-r border-slate-100">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleRow(os.id);
                          }}
                          className="p-1 rounded-md text-slate-500 hover:text-red-600 hover:bg-slate-200/60 transition-colors"
                          title={isExpanded ? 'Recolher detalhes' : 'Expandir detalhes'}
                        >
                          {isExpanded ? (
                            <ChevronUp className="w-4 h-4 text-red-600" />
                          ) : (
                            <ChevronDown className="w-4 h-4" />
                          )}
                        </button>
                      </td>

                      {/* ID */}
                      <td className="px-3 py-3 text-center font-bold text-slate-900 border-r border-slate-100">
                        {os.id}
                      </td>

                      {/* Cliente */}
                      <td className="px-3 py-3 font-semibold text-slate-800 border-r border-slate-100 truncate max-w-[200px]">
                        {os.cliente_nome || '-'}
                      </td>

                      {/* Status */}
                      <td className="px-3 py-3 text-center border-r border-slate-100">
                        <span
                          className="px-2.5 py-0.5 rounded-full text-3xs font-bold text-white shadow-2xs inline-block truncate max-w-[120px]"
                          style={{ backgroundColor: statusColor }}
                        >
                          {statusName}
                        </span>
                      </td>

                      {/* Data Atendimento */}
                      <td className="px-3 py-3 text-center border-r border-slate-100 font-medium whitespace-nowrap">
                        {formatDate(os.data_atendimento)}
                      </td>

                      {/* Veículo / Motorista */}
                      <td className="px-3 py-3 border-r border-slate-100 max-w-[180px] truncate">
                        <div className="font-medium text-slate-800">{os.veiculo_nome || '-'}</div>
                        <div className="text-3xs text-slate-400">{os.motorista_nome || 'Sem motorista'}</div>
                      </td>

                      {/* Total OS */}
                      <td className="px-3 py-3 text-center border-r border-slate-100 font-bold text-emerald-700 bg-emerald-50/20 whitespace-nowrap">
                        <MaskedValue
                          value={formatCurrency(os.total_os)}
                          isAllowed={canViewOrderPrices()}
                          mask="R$ •••••"
                        />
                      </td>

                      {/* Valor Líquido */}
                      <td className="px-3 py-3 text-center border-r border-slate-100 font-bold text-blue-700 bg-blue-50/30 whitespace-nowrap">
                        <MaskedValue
                          value={formatCurrency(os.valor_liquido)}
                          isAllowed={canViewOrderMargin()}
                          mask="R$ •••••"
                        />
                      </td>

                      {/* Botões de Ação */}
                      <td
                        className="px-3 py-2 text-center sticky right-0 bg-white z-10 border-l border-slate-200 shadow-2xs"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="flex items-center justify-center gap-1">
                          <ProtectedField permission="manage_orders" hideMode="remove">
                            <button
                              onClick={() => onEdit(os.id)}
                              className="p-1.5 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md transition-colors"
                              title="Editar Ordem de Serviço"
                            >
                              <Edit3 className="w-3.5 h-3.5" />
                            </button>
                          </ProtectedField>

                          <ProtectedField permission="print_os" hideMode="remove">
                            <button
                              onClick={() => onPrint(os.id)}
                              className="p-1.5 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md transition-colors"
                              title="Documento O.S (PDF / Imprimir)"
                            >
                              <FileText className="w-3.5 h-3.5" />
                            </button>
                          </ProtectedField>

                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (onEmitCte) onEmitCte(os);
                            }}
                            className="p-1.5 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md transition-colors"
                            title="Emitir CT-e (Conhecimento de Transporte)"
                          >
                            <Truck className="w-3.5 h-3.5" />
                          </button>

                          <button
                            onClick={() => onView(os.id)}
                            className="p-1.5 text-slate-700 hover:text-slate-900 hover:bg-slate-100 rounded-md transition-colors"
                            title="Visualizar O.S"
                          >
                            <Eye className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>

                    {/* Linha Expandida com Detalhes Completos */}
                    {isExpanded && (
                      <tr className="bg-slate-50/90 border-b-2 border-slate-300">
                        <td colSpan={9} className="p-4 sm:p-5">
                          <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm space-y-4">
                            <div className="flex items-center justify-between border-b border-slate-100 pb-2.5">
                              <span className="text-xs font-bold uppercase tracking-wider text-slate-700 flex items-center gap-1.5">
                                <FileText className="w-4 h-4 text-red-600" />
                                Detalhamento Expandido da Ordem de Serviço #{os.id}
                              </span>
                              <span className="text-2xs font-semibold text-slate-400">
                                Clique na seta para fechar este painel
                              </span>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                              {/* 1. Endereços & Rota */}
                              <div className="p-3 bg-slate-50 border border-slate-200/80 rounded-lg space-y-2">
                                <div className="text-2xs font-bold text-slate-500 uppercase flex items-center gap-1">
                                  <MapPin className="w-3.5 h-3.5 text-red-600" />
                                  Endereços & Rota
                                </div>
                                <div className="text-xs space-y-1">
                                  <div>
                                    <span className="font-bold text-slate-600">Retirada: </span>
                                    <span className="text-slate-800">{os.retirada || '-'}</span>
                                  </div>
                                  <div>
                                    <span className="font-bold text-slate-600">Entrega: </span>
                                    <span className="text-slate-800">{os.entrega || '-'}</span>
                                  </div>
                                  {os.descricao && (
                                    <div className="pt-1 border-t border-slate-200 text-slate-600 text-2xs italic">
                                      "{os.descricao}"
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* 2. Horários & Atendimento */}
                              <div className="p-3 bg-slate-50 border border-slate-200/80 rounded-lg space-y-2">
                                <div className="text-2xs font-bold text-slate-500 uppercase flex items-center gap-1">
                                  <Clock className="w-3.5 h-3.5 text-blue-600" />
                                  Horários do Atendimento
                                </div>
                                <div className="text-xs space-y-1">
                                  <div>
                                    <span className="font-bold text-slate-600">Início: </span>
                                    <span className="text-slate-800">{os.horario_inicio || '-'}</span>
                                  </div>
                                  <div>
                                    <span className="font-bold text-slate-600">Término: </span>
                                    <span className="text-slate-800">{os.horario_final || '-'}</span>
                                  </div>
                                  <div>
                                    <span className="font-bold text-slate-600">Total Horas: </span>
                                    <span className="text-slate-800 font-semibold">{os.hora_total || '-'}</span>
                                  </div>
                                </div>
                              </div>

                              {/* 3. Ajudantes & Equipe */}
                              <div className="p-3 bg-slate-50 border border-slate-200/80 rounded-lg space-y-2">
                                <div className="text-2xs font-bold text-slate-500 uppercase flex items-center gap-1">
                                  <Users className="w-3.5 h-3.5 text-purple-600" />
                                  Ajudantes Escalados
                                </div>
                                <div className="text-xs space-y-1">
                                  <div>
                                    <span className="font-bold text-slate-600">Quantidade: </span>
                                    <span className="text-slate-800 font-bold">{os.quantidade_ajudante || 0}</span>
                                  </div>
                                  <div>
                                    <span className="font-bold text-slate-600">Valor Unit.: </span>
                                    <span className="text-slate-800">
                                      <MaskedValue
                                        value={formatCurrency(os.valor_unit_ajud)}
                                        isAllowed={canViewOrderRepasse()}
                                        mask="R$ •••••"
                                      />
                                    </span>
                                  </div>
                                  <div>
                                    <span className="font-bold text-slate-600">Repasse Ajudantes: </span>
                                    <span className="text-slate-800 font-semibold">
                                      <MaskedValue
                                        value={formatCurrency(os.ajudante_repasse)}
                                        isAllowed={canViewOrderRepasse()}
                                        mask="R$ •••••"
                                      />
                                    </span>
                                  </div>
                                </div>
                              </div>

                              {/* 4. Medições & Documentação */}
                              <div className="p-3 bg-slate-50 border border-slate-200/80 rounded-lg space-y-2">
                                <div className="text-2xs font-bold text-slate-500 uppercase flex items-center gap-1">
                                  <FileCheck className="w-3.5 h-3.5 text-emerald-600" />
                                  Números de Medição
                                </div>
                                <div className="text-xs space-y-1">
                                  <div>
                                    <span className="font-bold text-slate-600">Medição Cliente: </span>
                                    <span className="text-slate-800">{os.medicoes_id || '-'}</span>
                                  </div>
                                  <div>
                                    <span className="font-bold text-slate-600">Medição Agregado: </span>
                                    <span className="text-slate-800">{os.medicoes_agregado || '-'}</span>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Detalhamento Financeiro & Ocorrências */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                              {/* Despesas & Ocorrências */}
                              <div className="p-3 bg-amber-50/50 border border-amber-200/80 rounded-lg space-y-2">
                                <div className="text-2xs font-bold text-amber-800 uppercase flex items-center gap-1">
                                  <AlertCircle className="w-3.5 h-3.5 text-amber-600" />
                                  Despesas Extras & Ocorrências
                                </div>
                                <div className="text-xs space-y-1.5 text-slate-800">
                                  <div>
                                    <span className="font-bold text-slate-700">Despesas Extras: </span>
                                    <span>{os.despesas || 'Nenhuma'}</span>
                                    {Number(os.valor_despesa) > 0 && (
                                      <span className="ml-1 font-bold text-rose-700">
                                        (
                                        <MaskedValue
                                          value={formatCurrency(os.valor_despesa)}
                                          isAllowed={canViewOrderPrices()}
                                          mask="R$ •••••"
                                        />
                                        )
                                      </span>
                                    )}
                                  </div>
                                  <div>
                                    <span className="font-bold text-slate-700">Ocorrências: </span>
                                    <span>{os.ocorrencias || 'Nenhuma ocorrência registrada'}</span>
                                  </div>
                                </div>
                              </div>

                              {/* Resumo dos Valores O.S */}
                              <div className="p-3 bg-slate-900 text-white rounded-lg space-y-2">
                                <div className="text-2xs font-bold text-slate-300 uppercase flex items-center gap-1 border-b border-slate-800 pb-1">
                                  <DollarSign className="w-3.5 h-3.5 text-emerald-400" />
                                  Composição Financeira da O.S
                                </div>
                                <div className="grid grid-cols-3 gap-2 text-center text-2xs pt-1">
                                  <div>
                                    <span className="text-slate-400 block">Total O.S</span>
                                    <span className="font-black text-emerald-400 text-xs">
                                      <MaskedValue
                                        value={formatCurrency(os.total_os)}
                                        isAllowed={canViewOrderPrices()}
                                        mask="R$ •••••"
                                      />
                                    </span>
                                  </div>
                                  <div>
                                    <span className="text-slate-400 block">Total Repasses</span>
                                    <span className="font-bold text-amber-400 text-xs">
                                      <MaskedValue
                                        value={formatCurrency(os.valor_total_repasses)}
                                        isAllowed={canViewOrderRepasse()}
                                        mask="R$ •••••"
                                      />
                                    </span>
                                  </div>
                                  <div>
                                    <span className="text-blue-300 block">Valor Líquido</span>
                                    <span className="font-black text-blue-300 text-xs">
                                      <MaskedValue
                                        value={formatCurrency(os.valor_liquido)}
                                        isAllowed={canViewOrderMargin()}
                                        mask="R$ •••••"
                                      />
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Ações Fiscais e Operacionais */}
                            <div className="pt-3 border-t border-slate-100 flex flex-wrap items-center justify-between gap-2">
                              <span className="text-2xs text-slate-500 font-medium">
                                Operação Rodoviária • SEFAZ Modelo 57
                              </span>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (onEmitCte) onEmitCte(os);
                                }}
                                className="px-3.5 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-xs font-bold flex items-center gap-1.5 shadow-sm transition-colors cursor-pointer"
                              >
                                <Truck className="w-3.5 h-3.5" />
                                Emitir CT-e a partir desta O.S
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>

          {/* Rodapé com Totais */}
          {ordens.length > 0 && !loading && (
            <tfoot className="bg-slate-900 text-white font-bold text-xs border-t-2 border-slate-300">
              <tr>
                <td colSpan={6} className="px-4 py-3 text-right uppercase tracking-wider text-slate-300">
                  Total Geral ({ordens.length} O.S):
                </td>
                <td className="px-3 py-3 text-center text-emerald-400 font-black text-sm whitespace-nowrap bg-slate-800 border-r border-slate-700">
                  <MaskedValue
                    value={formatCurrency(sumTotalOS)}
                    isAllowed={canViewOrderPrices()}
                    mask="R$ •••••"
                  />
                </td>
                <td className="px-3 py-3 text-center text-blue-300 font-black text-sm whitespace-nowrap bg-blue-950 border-r border-slate-700">
                  <MaskedValue
                    value={formatCurrency(sumTotalLiquido)}
                    isAllowed={canViewOrderMargin()}
                    mask="R$ •••••"
                  />
                </td>
                <td className="px-3 py-3 bg-slate-900 sticky right-0"></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* Seção de Paginação Estilo Screenshot */}
      {totalItems > 0 && !loading && (
        <div className="bg-white border-t border-slate-200 py-4 px-4 flex flex-col sm:flex-row items-center justify-between gap-3">
          {/* Seletor de Quantidade por Página */}
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <span>Exibir:</span>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="bg-white border border-slate-300 rounded-md px-2 py-1 text-xs text-slate-700 font-semibold focus:outline-none focus:border-red-500"
            >
              <option value={20}>20 por página</option>
              <option value={50}>50 por página</option>
              <option value={100}>100 por página</option>
              <option value={200}>200 por página</option>
            </select>
          </div>

          {/* Bloco Central de Paginação */}
          <div className="flex flex-col items-center gap-1.5">
            {/* Texto de Intervalo (ex: 1 a 50 de 346 registros) */}
            <div className="text-xs text-slate-400 font-medium">
              {startIndex + 1} a {endIndex} de {totalItems} registros
            </div>

            {/* Barra Numérica Integrada */}
            <div className="inline-flex items-center rounded-md border border-slate-300 bg-white text-xs font-semibold shadow-2xs overflow-hidden">
              {/* Primeira Página << */}
              <button
                type="button"
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="px-2.5 py-1.5 border-r border-slate-200 text-slate-600 hover:bg-slate-100 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
                title="Primeira Página"
              >
                <ChevronsLeft className="w-3.5 h-3.5" />
              </button>

              {/* Página Anterior < */}
              <button
                type="button"
                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                disabled={currentPage === 1}
                className="px-2.5 py-1.5 border-r border-slate-200 text-slate-600 hover:bg-slate-100 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
                title="Página Anterior"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
              </button>

              {/* Botões das Páginas */}
              {pageNumbers.map((page) => {
                const isActive = page === currentPage;
                return (
                  <button
                    key={page}
                    type="button"
                    onClick={() => setCurrentPage(page)}
                    className={`px-3 py-1.5 border-r border-slate-200 transition-colors font-bold ${
                      isActive
                        ? 'bg-red-500 text-white border-red-500 font-extrabold'
                        : 'text-slate-600 hover:bg-slate-100'
                    }`}
                  >
                    {page}
                  </button>
                );
              })}

              {/* Próxima Página > */}
              <button
                type="button"
                onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="px-2.5 py-1.5 border-r border-slate-200 text-slate-600 hover:bg-slate-100 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
                title="Próxima Página"
              >
                <ChevronRight className="w-3.5 h-3.5" />
              </button>

              {/* Última Página >> */}
              <button
                type="button"
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className="px-2.5 py-1.5 text-slate-600 hover:bg-slate-100 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
                title="Última Página"
              >
                <ChevronsRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          {/* Espaçador para alinhamento em desktop */}
          <div className="hidden sm:block w-28"></div>
        </div>
      )}
    </div>
  );
};

